import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MUITooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import classNames from 'classnames';

import infoIcon from '../../assets/svg/info.svg';
import infoIconLight from '../../assets/svg/info-light.svg';
import infoIconSmall from '../../assets/svg/info-small.svg';
import useStyles from './tooltip.styles';

export function Tooltip({
  text,
  className,
  placement,
  light,
  label,
  onClick,
  onMouseEnter,
  size,
  labelNextToText,
  height,
  smallIcon,
  clickIcon,
  iconSize: icSize,
  formField,
  toggleOnClick,
  ariaLabel,
  ...props
}) {
  const styleClasses = useStyles({ size, height });
  const iconSize = icSize || 'smallIcon';
  const [isOpen, setIsOpen] = useState(false);
  // Whether the open tooltip was pinned by a click (survives mouse-leave) vs.
  // merely hover-opened. A ref, since it shouldn't itself trigger a render —
  // `isOpen` is what drives rendering.
  const isPinned = useRef(false);
  // Set when a click closes the tooltip while the pointer is still on the trigger: MUI's hover-open
  // timer from that pointer's arrival may still be pending and would reopen it. Cleared on leave.
  const ignoreHoverOpen = useRef(false);
  // The tooltip renders in a portal outside the trigger, so clicks on its text would otherwise
  // count as "away" and blur the trigger.
  const noteRef = useRef(null);
  const isInsideNote = (target) => Boolean(target && noteRef.current?.contains(target));
  const close = () => {
    isPinned.current = false;
    setIsOpen(false);
  };

  useEffect(() => {
    if (!toggleOnClick || !isOpen) {
      return undefined;
    }
    // MUI v4's Tooltip has no Escape handling of its own.
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [toggleOnClick, isOpen]);

  // Controlled mode only when click-toggling is requested; hover keeps working
  // because MUI fires onOpen/onClose on hover/focus.
  // toggleOnClick semantics: the trigger is a button, so keyboard focus and taps work too. A click
  // pins the tooltip open (survives mouse-leave and MUI's touch auto-close) until Escape, a click
  // outside the tooltip, a second click, or focus moving to another control. Clicks inside the
  // tooltip keep it open so its text can be selected; hover open/close still works while unpinned.
  const controlledProps = toggleOnClick
    ? {
        open: isOpen,
        onOpen: () => {
          if (!ignoreHoverOpen.current) {
            setIsOpen(true);
          }
        },
        onClose: () => {
          ignoreHoverOpen.current = false;
          if (!isPinned.current) {
            setIsOpen(false);
          }
        },
        PopperProps: { ref: noteRef }
      }
    : {};
  const handleClick = (e) => {
    if (toggleOnClick) {
      if (isPinned.current) {
        close();
        ignoreHoverOpen.current = true;
      } else {
        ignoreHoverOpen.current = false;
        isPinned.current = true;
        setIsOpen(true);
      }
    }
    onClick(e);
  };
  const handleBlur = (e) => {
    // A click on plain text in the tooltip blurs the trigger with no relatedTarget; that case is
    // left to the click-away handler. Focus moving to another control closes the tooltip.
    if (e.relatedTarget && !isInsideNote(e.relatedTarget)) {
      close();
    }
  };

  const icon = smallIcon ? (
    <img className={classNames(className, styleClasses[iconSize])} src={smallIcon.src || infoIconSmall} alt={text} />
  ) : (
    <img className={classNames(className, styleClasses.info)} src={light ? infoIconLight : infoIcon} alt={text} />
  );
  const labelClassName = classNames(className, styleClasses.desktopLabel, {
    [styleClasses.desktopLabelOnDark]: light,
    [styleClasses.labelNextToText]: labelNextToText
  });
  const labelContent = (
    <>
      {label}
      {clickIcon && <img src={clickIcon} alt={text} className={classNames(className, styleClasses[iconSize])} />}
    </>
  );

  let trigger;
  if (!label) {
    trigger = toggleOnClick ? (
      <button
        type="button"
        aria-label={ariaLabel}
        className={classNames(styleClasses.toggleButton, styleClasses.toggleIconButton)}
        onClick={handleClick}
        onBlur={handleBlur}
        onMouseEnter={onMouseEnter}
      >
        {icon}
      </button>
    ) : (
      <span className={styleClasses.iconContainer} onClick={handleClick} onMouseEnter={onMouseEnter}>
        {icon}
      </span>
    );
  } else {
    trigger = (
      <div
        {...props}
        className={classNames(styleClasses.labelWrapper, {
          [styleClasses.formField]: formField
        })}
      >
        {toggleOnClick ? (
          <button
            type="button"
            aria-label={ariaLabel}
            className={classNames(styleClasses.toggleButton, labelClassName)}
            onClick={handleClick}
            onBlur={handleBlur}
          >
            {labelContent}
          </button>
        ) : (
          <span className={labelClassName} onClick={handleClick}>
            {labelContent}
          </span>
        )}
      </div>
    );
  }

  const tooltip = (
    <MUITooltip
      classes={{ tooltip: styleClasses.tooltip, arrow: styleClasses.arrow }}
      title={text}
      placement={placement}
      interactive
      arrow
      {...controlledProps}
    >
      {trigger}
    </MUITooltip>
  );

  if (!toggleOnClick) {
    return tooltip;
  }
  return (
    <ClickAwayListener
      onClickAway={(e) => {
        if (!isInsideNote(e.target)) {
          close();
        }
      }}
    >
      {tooltip}
    </ClickAwayListener>
  );
}

Tooltip.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  className: PropTypes.string,
  placement: PropTypes.string,
  light: PropTypes.bool,
  label: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  labelNextToText: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'large']),
  height: PropTypes.number,
  smallIcon: PropTypes.object,
  clickIcon: PropTypes.string,
  autoCompleteField: PropTypes.bool,
  formField: PropTypes.bool,
  iconSize: PropTypes.oneOf(['icon16', 'smallIcon', 'icon10']),
  toggleOnClick: PropTypes.bool,
  // Accessible name for the toggleOnClick button; defaults to its icon's alt text or label.
  ariaLabel: PropTypes.string
};

Tooltip.defaultProps = {
  className: '',
  label: '',
  onClick: () => {},
  onMouseEnter: () => {},
  placement: 'right-start',
  light: false,
  labelNextToText: false,
  size: 'large',
  formField: false,
  toggleOnClick: false
};

export default Tooltip;
